import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Save, X, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import SymptomAnalytics from "./admin/SymptomAnalytics";
import GMBPostStudio from "./admin/GMBPostStudio";
import EmergencyAlertPoller from "./admin/EmergencyAlertPoller";
import ImageUpload from "../components/site/ImageUpload";
import RichTextEditor from "../components/site/RichTextEditor";
import { CASE_CATEGORIES } from "./EyeCases";

// Roman-friendly kebab-case slugifier — matches the backend `normalize_slug`
// closely enough that admins see the same URL that will be saved.
// (The backend uses `unidecode` for full transliteration; this JS version is a
// best-effort preview so admins don't have to guess.)
// If the input is dominantly Unicode (Hindi/Devanagari/etc.), we return "" so the
// admin form leaves the slug blank and the server-side unidecode fallback (which
// transliterates the *title*) does the heavy lifting.
const slugifyClient = (text = "") => {
  const raw = (text || "").toString();
  const cleaned = raw
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 120);
  // If stripping non-ASCII leaves us with < 3 usable chars (e.g. a Hindi-only
  // title), bail out — we'd rather send "" and let the backend transliterate.
  if (cleaned.replace(/-/g, "").length < 3) return "";
  return cleaned;
};

// Client-side mirror of backend `parse_short_url` — used only for the live
// preview badge in the Shorts CMS form so the admin sees the detected platform
// before they click Save. The backend re-parses on write so this can stay tiny.
const detectShortClient = (url = "") => {
  const u = (url || "").trim();
  if (!u) return { platform: "", video_id: "", thumbnail: "" };
  const yt =
    u.match(/youtube\.com\/shorts\/([\w-]{6,})/) ||
    u.match(/youtube\.com\/embed\/([\w-]{6,})/) ||
    u.match(/youtube\.com\/watch\?v=([\w-]{6,})/) ||
    u.match(/youtu\.be\/([\w-]{6,})/);
  if (yt) {
    const vid = yt[1];
    return {
      platform: "youtube_short",
      video_id: vid,
      thumbnail: `https://i.ytimg.com/vi/${vid}/hqdefault.jpg`,
    };
  }
  const ig = u.match(/instagram\.com\/(?:reel|reels|p|tv)\/([\w-]+)/);
  if (ig) return { platform: "instagram_reel", video_id: ig[1], thumbnail: "" };
  return { platform: "", video_id: "", thumbnail: "" };
};

/**
 * RefreshPublicationsBtn — one-click button that triggers the backend
 * enrichment endpoint (POST /api/admin/publications/enrich). Streams progress
 * via a toast because the LLM-style API can take 20-40 s to walk every paper.
 */
const RefreshPublicationsBtn = () => {
  const [busy, setBusy] = useState(false);
  const run = async (force = false) => {
    setBusy(true);
    const tid = toast.loading("Fetching PMID · DOI · citations from Semantic Scholar and PubMed…");
    try {
      const r = await api.post(`/admin/publications/enrich${force ? "?force=true" : ""}`);
      toast.success(`Enriched ${r.data.enriched} / ${r.data.checked} publications (${r.data.skipped_no_match} skipped).`, { id: tid });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Enrichment failed.", { id: tid });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => run(false)}
        disabled={busy}
        data-testid="publications-refresh-btn"
        className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {busy ? "Refreshing…" : "Refresh citations"}
      </button>
      <button
        type="button"
        onClick={() => run(true)}
        disabled={busy}
        title="Ignore the 7-day cache and refresh every publication"
        className="text-[10.5px] text-muted-foreground hover:text-[hsl(var(--primary))] underline underline-offset-2"
      >
        force
      </button>
    </div>
  );
};

const BLANK = {
  blog: { slug: "", title: "", excerpt: "", body: "", cover_image: "", category: "General", tags: [], reading_time: 5, featured: false, answer_summary: "", medically_reviewed: "", related_expertise: [], related_articles: [], has_hindi: false, hindi_title: "", hindi_excerpt: "", hindi_body: "", hindi_answer_summary: "" },
  faqs: { category: "General", q: "", a: "" },
  testimonials: { name: "", location: "", procedure: "", rating: 5, quote: "", video_id: "", video_title: "" },
  media: { type: "article", outlet: "", title: "", date: "", link: "", summary: "", pmid: "", doi: "", citations: null, citations_updated_at: "", semantic_scholar_id: "" },
  locations: { slug: "", city: "", state: "Uttar Pradesh", tagline: "", intro: "", distance_km: "", travel_note: "", landmarks: [], faqs: [], lat: null, lng: null },
  reviews: { author_name: "", rating: 5, text: "", relative_time: "", profile_photo_url: "", source: "manual", verified: true, order: 0 },
  guides: { slug: "", title: "", description: "", category: "General", file_url: "", cover_image: "", pages: 0, language: "en", order: 0 },
  shorts: { url: "", title: "", caption: "", date: "", featured: false, order: 0 },
  cases: {
    slug: "", title: "", category: "Fundus Photography", summary: "", description: "",
    image_url: "", image_alt: "", extra_images: [], external_links: [],
    diagnosis: "", patient_context: "", related_expertise: [],
    submitted_by: "shantanu", published: true, featured: false, order: 0,
  },
};

const listFields = {
  blog: ["slug", "title", "category"],
  hub: ["slug", "title", "category"],
  faqs: ["category", "q"],
  testimonials: ["name", "procedure", "location"],
  media: ["type", "outlet", "title", "date"],
  locations: ["slug", "city", "state"],
  reviews: ["author_name", "rating", "source"],
  guides: ["slug", "title", "category"],
  shorts: ["platform", "caption", "featured"],
  cases: ["slug", "title", "category", "submitted_by"],
};

const Section = ({ collection, blank, renderForm }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/admin/${collection}`).then((r) => setItems(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...blank }); setOpen(true); };
  const openEdit = (it) => { setEditing({ ...it }); setOpen(true); };
  const close = () => { setOpen(false); setEditing(null); };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/${collection}/${editing.id}`, editing);
      else await api.post(`/admin/${collection}`, editing);
      toast.success("Saved");
      close();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/${collection}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">{items.length} items</div>
        <button className="btn-primary !py-2 !px-4 text-xs" onClick={openNew} data-testid={`${collection}-new-btn`}><Plus size={14} /> New</button>
      </div>
      <div className="border border-border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {listFields[collection].map((f) => <TableHead key={f}>{f}</TableHead>)}
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`${collection}-row-${it.id}`}>
                {listFields[collection].map((f) => (
                  <TableCell key={f} className="text-sm max-w-md truncate">{Array.isArray(it[f]) ? it[f].join(", ") : String(it[f] ?? "")}</TableCell>
                ))}
                <TableCell>
                  <div className="flex gap-2">
                    <button className="text-xs text-[hsl(var(--primary))]" onClick={() => openEdit(it)} data-testid={`${collection}-edit-${it.id}`}>Edit</button>
                    <button className="text-xs text-destructive" onClick={() => remove(it.id)} data-testid={`${collection}-delete-${it.id}`}><Trash2 size={12} /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "New"} {collection}</DialogTitle>
            <DialogDescription className="sr-only">
              Edit the {collection} record and save your changes.
            </DialogDescription>
          </DialogHeader>
          {editing && renderForm(editing, setEditing)}
          <DialogFooter>
            <button className="btn-ghost !py-2 !px-4 text-xs" onClick={close} data-testid={`${collection}-cancel`}><X size={14} /> Cancel</button>
            <button className="btn-primary !py-2 !px-4 text-xs" onClick={save} data-testid={`${collection}-save`}><Save size={14} /> Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children, testid, hint }) => (
  <div className="space-y-1" data-testid={testid ? `field-${testid}` : undefined}>
    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground/80 leading-snug pt-0.5">{hint}</p>}
  </div>
);

/**
 * HindiTranslateButton — one-click auto-translate English blog draft (title,
 * excerpt, body, answer_summary) into Hindi via the /api/admin/translate-blog
 * endpoint (Claude Sonnet 4.6). On success, patches the four hindi_* fields on
 * the editing draft. Never overwrites without confirmation if Hindi fields
 * already contain text.
 */
const HindiTranslateButton = ({ draft, setDraft }) => {
  const [busy, setBusy] = useState(false);
  const hasExistingHindi = !!(draft.hindi_title || draft.hindi_body || draft.hindi_excerpt || draft.hindi_answer_summary);

  const run = async () => {
    if (!draft.title?.trim()) {
      toast.error("Add an English title first — that's the minimum required to translate.");
      return;
    }
    if (hasExistingHindi && !window.confirm("This will overwrite the existing Hindi fields with a fresh translation. Continue?")) {
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/translate-blog", {
        title: draft.title || "",
        excerpt: draft.excerpt || "",
        body: draft.body || "",
        answer_summary: draft.answer_summary || "",
      });
      setDraft({
        ...draft,
        has_hindi: true,
        hindi_title: r.data.hindi_title || "",
        hindi_excerpt: r.data.hindi_excerpt || "",
        hindi_body: r.data.hindi_body || "",
        hindi_answer_summary: r.data.hindi_answer_summary || "",
      });
      toast.success("Translated to Hindi — review and save.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Translation failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      data-testid="blog-auto-translate-btn"
      className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
      {busy ? "Translating…" : "Auto-translate from English"}
    </button>
  );
};

/**
 * FaqSection — bespoke, "foolproof" CRUD for the FAQ collection:
 * suggested categories dropdown, character counters, required-field guard,
 * and inline preview of how the answer will render on the public FAQ page.
 */
const FaqSection = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/faqs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const existingCategories = useMemo(
    () => Array.from(new Set(items.map((i) => i.category).filter(Boolean))).sort(),
    [items]
  );

  const openNew = () => { setEditing({ category: existingCategories[0] || "Cataract", q: "", a: "" }); setOpen(true); };
  const openEdit = (it) => { setEditing({ ...it }); setOpen(true); };
  const close = () => { setOpen(false); setEditing(null); };

  const save = async () => {
    const q = (editing.q || "").trim();
    const a = (editing.a || "").trim();
    const category = (editing.category || "").trim();
    if (!q || !a || !category) {
      toast.error("Category, Question and Answer are all required.");
      return;
    }
    if (q.length < 8) { toast.error("Question is too short — please add more detail."); return; }
    if (a.length < 15) { toast.error("Answer is too short — please write at least a full sentence."); return; }
    try {
      const body = { ...editing, q, a, category };
      if (editing.id) await api.put(`/admin/faqs/${editing.id}`, body);
      else await api.post("/admin/faqs", body);
      toast.success("FAQ saved");
      close();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try { await api.delete(`/admin/faqs/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">{items.length} FAQs · {existingCategories.length} categories</div>
        <button className="btn-primary !py-2 !px-4 text-xs" onClick={openNew} data-testid="faqs-new-btn"><Plus size={14} /> New FAQ</button>
      </div>

      <div className="border border-border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Category</TableHead>
              <TableHead>Question</TableHead>
              <TableHead className="w-24">Answer</TableHead>
              <TableHead className="w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`faqs-row-${it.id}`}>
                <TableCell><span className="text-xs px-2 py-0.5 rounded-full border border-border">{it.category}</span></TableCell>
                <TableCell className="text-sm">{it.q}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{(it.a || "").length} chars</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button className="text-xs text-[hsl(var(--primary))]" onClick={() => openEdit(it)} data-testid={`faqs-edit-${it.id}`}>Edit</button>
                    <button className="text-xs text-destructive" onClick={() => remove(it.id)} data-testid={`faqs-delete-${it.id}`}><Trash2 size={12} /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No FAQs yet — click "New FAQ" to add your first.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "New"} FAQ</DialogTitle>
            <DialogDescription className="sr-only">
              Edit the FAQ entry and save your changes.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Field
                label="Category"
                hint="Pick an existing topic for consistent grouping. Type a new one only if the topic is genuinely different."
              >
                <div className="flex gap-2">
                  <select
                    data-testid="faqs-category-select"
                    className="flex-1 border border-input rounded-md h-10 px-3 bg-background text-sm"
                    value={existingCategories.includes(editing.category) ? editing.category : "__custom__"}
                    onChange={(x) => setEditing({ ...editing, category: x.target.value === "__custom__" ? "" : x.target.value })}
                  >
                    {existingCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                    <option value="__custom__">+ New category…</option>
                  </select>
                  {!existingCategories.includes(editing.category) && (
                    <Input
                      data-testid="faqs-category-new"
                      placeholder="New category name"
                      value={editing.category}
                      onChange={(x) => setEditing({ ...editing, category: x.target.value })}
                    />
                  )}
                </div>
              </Field>

              <Field
                label={`Question (${(editing.q || "").length}/140)`}
                hint="Write the question exactly as a patient would ask it. Keep it under 140 characters for best display."
              >
                <Input
                  data-testid="faqs-input-q"
                  value={editing.q}
                  maxLength={140}
                  onChange={(x) => setEditing({ ...editing, q: x.target.value })}
                  placeholder="e.g. How long does cataract surgery take?"
                />
              </Field>

              <Field
                label={`Answer (${(editing.a || "").length} chars)`}
                hint="Aim for 40–400 characters. Use blank lines between paragraphs — they will render on the public page."
              >
                <Textarea
                  data-testid="faqs-input-a"
                  rows={6}
                  value={editing.a}
                  onChange={(x) => setEditing({ ...editing, a: x.target.value })}
                  placeholder="Write a clear, patient-friendly answer in plain English."
                />
              </Field>

              {editing.a && (
                <div className="rounded-lg border border-dashed border-border p-4 bg-[hsl(var(--secondary))]/40">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] mb-2">Preview</div>
                  <div className="font-medium">{editing.q || "Your question will appear here"}</div>
                  <p className="mt-2 text-sm text-[hsl(var(--foreground))]/75 whitespace-pre-line leading-relaxed">{editing.a}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button className="btn-ghost !py-2 !px-4 text-xs" onClick={close} data-testid="faqs-cancel"><X size={14} /> Cancel</button>
            <button className="btn-primary !py-2 !px-4 text-xs" onClick={save} data-testid="faqs-save"><Save size={14} /> Save FAQ</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * IndexNowPanel — small admin control that shows the IndexNow key config
 * status (whether the key file is verifiable at the domain root) and lets
 * the admin manually resubmit every public URL to Bing / Yandex / Seznam.
 * New / updated CMS items are already auto-pinged on save — this button is
 * for post-deploy or bulk-import scenarios.
 */
const IndexNowPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/indexnow/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const resubmit = async () => {
    setBusy(true);
    const tid = toast.loading("Submitting every URL to Bing / Yandex / Seznam…");
    try {
      const r = await api.post("/admin/indexnow/resubmit-all");
      if (r.data?.ok) {
        toast.success(`Submitted ${r.data.submitted} URLs to IndexNow (HTTP ${r.data.status}).`, { id: tid });
      } else if (r.data?.pending_verification) {
        toast(
          (t) => (
            <div className="text-xs leading-relaxed">
              <div className="font-semibold text-[hsl(var(--foreground))] mb-1">
                Bing rejected the submission ({r.data.submitted} URLs) — <code>UserForbiddedToAccessSite</code>
              </div>
              <div className="text-muted-foreground mb-2">
                Your key file is reachable, but this domain isn't claimed in <b>Bing Webmaster Tools</b> yet — Bing IndexNow refuses submissions until then. This is normal in the first 24&nbsp;h; if it's been longer, action is needed.
              </div>
              <ol className="list-decimal ml-4 space-y-0.5 text-muted-foreground">
                <li>Open <a href="https://www.bing.com/webmasters" target="_blank" rel="noreferrer" className="underline text-[hsl(var(--primary))]">bing.com/webmasters</a> and sign in</li>
                <li>Add site: <code>https://drshantanugupta.com</code></li>
                <li>Verify ownership (DNS TXT, meta tag, or BingSiteAuth.xml)</li>
                <li>Return here and click <b>1-Click Index</b> again — it should return HTTP 200/202</li>
              </ol>
            </div>
          ),
          { id: tid, duration: 20000 }
        );
      } else {
        toast.error(
          `IndexNow returned HTTP ${r.data?.status ?? "?"} — ${
            r.data?.status === 422 ? "key not verified at domain root. Deploy to production first." : "check server logs"
          }.`,
          { id: tid }
        );
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "IndexNow submit failed.", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  if (!status.configured) {
    return (
      <div className="mb-6 p-3 rounded-lg border border-destructive/40 bg-destructive/5 text-xs text-destructive">
        IndexNow is not configured — <code>INDEXNOW_KEY</code> is missing from backend .env.
      </div>
    );
  }

  const bingState = status.bing_state; // "accepting" | "pending_bwt_verification" | "key_mismatch" | "http_xxx" | "unknown"
  const yandexState = status.yandex_state; // "accepting" | "http_xxx" | ...
  const isAccepting  = bingState === "accepting";
  const isPendingBWT = bingState === "pending_bwt_verification";
  const isKeyMismatch = bingState === "key_mismatch";
  const yandexOk = yandexState === "accepting";

  const badgeLabel = !status.verified
    ? "key file not deployed"
    : isAccepting          ? "verified · Bing + Yandex accepting"
    : isPendingBWT && yandexOk ? "Yandex live · Bing verifying"
    : isPendingBWT         ? "action needed · claim in Bing Webmaster Tools"
    : isKeyMismatch        ? "key mismatch on host"
                           : "checking…";
  const badgeGood = status.verified && (isAccepting || (isPendingBWT && yandexOk));

  return (
    <div
      className="mb-6 p-4 rounded-xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 flex items-center justify-between gap-4 flex-wrap"
      data-testid="indexnow-panel"
    >
      <div className="text-xs min-w-0">
        <div className="font-medium text-[hsl(var(--foreground))] flex items-center gap-2 flex-wrap">
          IndexNow · instant Bing / Perplexity / ChatGPT-search discovery
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-[0.16em] ${
              badgeGood
                ? "bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]"
                : "bg-destructive/12 text-destructive"
            }`}
            data-testid="indexnow-status-badge"
          >
            {badgeLabel}
          </span>
        </div>
        <div className="text-muted-foreground mt-1 truncate">
          Every save is auto-pinged. Use the button for post-deploy bulk resubmission. Host: <code>{status.host}</code>
        </div>
        {!status.verified && (
          <div className="text-[11px] text-muted-foreground/80 mt-1">
            Deploy this build to <code>{status.host}</code> so the key file becomes reachable — then verified will flip to true.
          </div>
        )}
        {status.verified && isPendingBWT && (
          <div className="text-[11px] text-destructive/90 mt-2 leading-relaxed max-w-xl">
            {yandexOk ? (
              <>
                <b>Yandex + Naver + Seznam are accepting our submissions</b> (HTTP 202) — your URLs are being discovered by ~20% of AI-search traffic already. However, Bing IndexNow returns <code>UserForbiddedToAccessSite</code> despite BWT showing verified — this is a known Bing-side sync bug (community reports of 24-72 h delays even after BWT green).{" "}
              </>
            ) : (
              <>Your key file is reachable — but Bing IndexNow returns <code>UserForbiddedToAccessSite</code>.{" "}</>
            )}
            <a href="https://www.bing.com/webmasters" target="_blank" rel="noreferrer" className="underline text-[hsl(var(--primary))] font-medium">
              Open Bing Webmaster Tools
            </a>
            {" "}→ if BWT already shows Verified, try removing and re-adding the site with XML File Authentication to force a fresh sync.
          </div>
        )}
        {status.verified && isKeyMismatch && (
          <div className="text-[11px] text-destructive/90 mt-2 leading-relaxed max-w-xl">
            Bing returned HTTP 422 (key mismatch). The file at <code>{status.key_location}</code> does not match the configured <code>INDEXNOW_KEY</code>. Re-deploy so the file is regenerated.
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={resubmit}
        disabled={busy}
        data-testid="indexnow-resubmit-btn"
        className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {busy ? "Submitting…" : "Resubmit all URLs"}
      </button>
    </div>
  );
};

/**
 * GscPanel — mirrors IndexNowPanel for Google Search Console. Every CMS save
 * already re-submits the sitemap automatically (debounced), so the button is
 * really only useful for post-deploy or when the admin wants to force a
 * re-crawl right now.
 */
const GscPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/gsc/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const refresh = async () => {
    setBusy(true);
    const tid = toast.loading("Submitting sitemap to Google Search Console…");
    try {
      const r = await api.post("/admin/gsc/refresh");
      if (r.data?.ok) {
        toast.success(`Sitemap submitted to Google (HTTP ${r.data.status}).`, { id: tid });
      } else {
        // 403 → permission missing, 404 → property/feedpath wrong, etc.
        const status = r.data?.status;
        const err = r.data?.error || "Google refresh failed.";
        toast(
          (t) => (
            <div className="text-xs leading-relaxed">
              <div className="font-semibold text-[hsl(var(--foreground))] mb-1">
                Google returned {status ? `HTTP ${status}` : "an error"}
              </div>
              <div className="text-muted-foreground mb-1">{err}</div>
              {status === 403 && (
                <div className="text-muted-foreground">
                  Fix: add the service-account email as a <b>Full</b> user on the property in{" "}
                  <a href="https://search.google.com/search-console/users" target="_blank" rel="noreferrer" className="underline text-[hsl(var(--primary))]">
                    Search Console → Settings → Users
                  </a>.
                </div>
              )}
            </div>
          ),
          { id: tid, duration: 15000 }
        );
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Google refresh failed.", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  if (!status.configured) {
    return (
      <div className="mb-6 p-3 rounded-lg border border-muted bg-muted/30 text-xs text-muted-foreground" data-testid="gsc-panel-unconfigured">
        <div className="font-medium mb-1 text-[hsl(var(--foreground))]">Google Search Console — not configured</div>
        <div>
          Ask the developer to add a service-account JSON to <code>GSC_SERVICE_ACCOUNT_JSON</code> in the backend .env. Reason: <code>{status.error || "credentials missing"}</code>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mb-6 p-4 rounded-xl border border-[hsl(var(--accent))]/25 bg-[hsl(var(--accent))]/5 flex items-center justify-between gap-4 flex-wrap"
      data-testid="gsc-panel"
    >
      <div className="text-xs min-w-0">
        <div className="font-medium text-[hsl(var(--foreground))] flex items-center gap-2 flex-wrap">
          Google Search Console · sitemap auto-refresh
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-[0.16em] bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))]">
            configured
          </span>
        </div>
        <div className="text-muted-foreground mt-1 truncate">
          Every CMS save re-submits <code>{status.sitemap_url}</code> to Google. Property: <code>{status.site_url}</code>
        </div>
        <div className="text-[11px] text-muted-foreground/80 mt-1 truncate">
          Service account: <code>{status.service_account_email}</code>
        </div>
      </div>
      <button
        type="button"
        onClick={refresh}
        disabled={busy}
        data-testid="gsc-refresh-btn"
        className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {busy ? "Submitting…" : "1-Click Google Refresh"}
      </button>
    </div>
  );
};

/**
 * GoogleReviewsPanel — shows the current live Google rating + count and
 * exposes a "Refresh now" button that forces the backend to re-fetch from
 * Places API (bypassing the 6-hour cache).  Use after asking patients to
 * leave a review so the numbers on the site update immediately.
 */
const GoogleReviewsPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/reviews/google").then((r) => setData(r.data)).catch(() => {});
  }, []);
  const refresh = async () => {
    setBusy(true);
    const tid = toast.loading("Refreshing Google reviews…");
    try {
      const r = await api.post("/admin/reviews/refresh");
      setData(r.data);
      toast.success(`Refreshed — ${r.data.rating}★ · ${r.data.review_count} reviews`, { id: tid });
    } catch (e) {
      toast.error("Refresh failed — check API key + billing", { id: tid });
    } finally {
      setBusy(false);
    }
  };
  if (!data) return null;
  return (
    <div
      className="p-4 rounded-xl border border-[hsl(var(--accent))]/25 bg-[hsl(var(--accent))]/5 flex items-center justify-between gap-4 flex-wrap"
      data-testid="google-reviews-panel"
    >
      <div className="text-xs min-w-0">
        <div className="font-medium text-[hsl(var(--foreground))] flex items-center gap-2">
          Google Reviews · live
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-[0.16em] bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))]">
            {Number(data.rating || 0).toFixed(1)}★ · {data.review_count}
          </span>
        </div>
        <div className="text-muted-foreground mt-1">
          Cached for 6 hours · Force a refresh after asking patients to leave a review.
        </div>
      </div>
      <button
        type="button"
        onClick={refresh}
        disabled={busy}
        data-testid="google-reviews-refresh-btn"
        className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {busy ? "Refreshing…" : "Refresh now"}
      </button>
    </div>
  );
};

const Admin = () => {  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [appointments, setAppointments] = useState([]);
  useEffect(() => { api.get("/admin/appointments").then((r) => setAppointments(r.data)).catch(() => {}); }, []);

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))]/30" data-testid="admin-page">
      <header className="border-b border-border bg-card">
        <div className="container-page flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center relative">
              <span className="absolute w-3.5 h-3.5 rounded-full bg-[hsl(var(--accent))]" />
              <span className="absolute w-1 h-1 rounded-full bg-white" />
            </div>
            <div>
              <div className="font-serif text-lg">Shantanu Netralaya CMS</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Signed in as {user?.email}</div>
            </div>
          </div>
          <div className="flex gap-3 items-center">
            <Link to="/" className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))] inline-flex items-center gap-1" data-testid="admin-view-site">View site <ExternalLink size={12} /></Link>
            <button onClick={doLogout} className="btn-ghost !py-2 !px-4 text-xs" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="container-page py-10">
        <h1 className="font-serif text-4xl">Content Management</h1>
        <p className="text-sm text-muted-foreground mt-1">Create, edit and remove content — no coding required.</p>

        <div className="mt-8"><IndexNowPanel /></div>
        <div className="mt-4"><GscPanel /></div>
        <div className="mt-4"><GoogleReviewsPanel /></div>
        <EmergencyAlertPoller />

        <Tabs defaultValue="blog" className="mt-4">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="hub" data-testid="tab-hub">Content Hub</TabsTrigger>
            <TabsTrigger value="faqs" data-testid="tab-faqs">FAQs</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
            <TabsTrigger value="shorts" data-testid="tab-shorts">Shorts & Reels</TabsTrigger>
            <TabsTrigger value="locations" data-testid="tab-locations">Locations</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="guides" data-testid="tab-guides">Guides</TabsTrigger>
            <TabsTrigger value="cases" data-testid="tab-cases">Eye Cases</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments ({appointments.length})</TabsTrigger>
            <TabsTrigger value="symptom-analytics" data-testid="tab-symptom-analytics">Symptom Checker</TabsTrigger>
            <TabsTrigger value="gmb-studio" data-testid="tab-gmb-studio">GMB Studio</TabsTrigger>
          </TabsList>

          <TabsContent value="blog" className="mt-6">
            <Section
              collection="blog"
              blank={BLANK.blog}
              renderForm={(e, setE) => (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Slug" hint={`URL: /blog/${slugifyClient(e.slug || e.title) || "auto-generated on save"} — Hindi & spaces auto-normalised on save.`}><Input data-testid="blog-input-slug" value={e.slug} onChange={(x) => setE({ ...e, slug: x.target.value })} placeholder="Leave blank to auto-generate from title" /></Field>
                  <Field label="Category"><Input data-testid="blog-input-category" value={e.category} onChange={(x) => setE({ ...e, category: x.target.value })} /></Field>
                  <div className="col-span-2"><Field label="Title"><Input data-testid="blog-input-title" value={e.title} onChange={(x) => {
                    const wasAutoSlug = !e.slug || e.slug === slugifyClient(e.title);
                    setE({ ...e, title: x.target.value, ...(wasAutoSlug ? { slug: slugifyClient(x.target.value) } : {}) });
                  }} /></Field></div>
                  <div className="col-span-2"><Field label="Excerpt"><Textarea data-testid="blog-input-excerpt" rows={2} value={e.excerpt} onChange={(x) => setE({ ...e, excerpt: x.target.value })} /></Field></div>
                  <div className="col-span-2"><Field label="Body" hint="Rich text — supports headings, lists, tables, links, images and embedded YouTube. Old markdown posts remain compatible."><RichTextEditor value={e.body || ""} onChange={(html) => setE({ ...e, body: html })} placeholder="Start writing the article body…" testId="blog-input-body" minHeight={360} /></Field></div>
                  <Field label="Cover image URL"><Input data-testid="blog-input-cover" value={e.cover_image} onChange={(x) => setE({ ...e, cover_image: x.target.value })} placeholder="Paste URL or upload below" /></Field>
                  <div className="col-span-2"><ImageUpload label="Or upload cover image" value={e.cover_image} onChange={(url) => setE({ ...e, cover_image: url })} /></div>
                  <Field label="Tags (comma-separated)"><Input data-testid="blog-input-tags" value={(e.tags || []).join(", ")} onChange={(x) => setE({ ...e, tags: x.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} /></Field>
                  <Field label="Reading time (min)"><Input data-testid="blog-input-reading-time" type="number" value={e.reading_time} onChange={(x) => setE({ ...e, reading_time: parseInt(x.target.value || 0, 10) })} /></Field>
                  <Field label="Featured"><select data-testid="blog-input-featured" className="w-full border border-input rounded-md h-10 px-3 bg-background" value={e.featured ? "1" : "0"} onChange={(x) => setE({ ...e, featured: x.target.value === "1" })}><option value="0">No</option><option value="1">Yes</option></select></Field>
                  <Field label="Medically reviewed (YYYY-MM-DD)"><Input data-testid="blog-input-reviewed" type="date" value={e.medically_reviewed || ""} onChange={(x) => setE({ ...e, medically_reviewed: x.target.value })} /></Field>
                  <div className="col-span-2"><Field label="Answer summary (TL;DR for AI search)"><Textarea data-testid="blog-input-answer" rows={3} value={e.answer_summary || ""} onChange={(x) => setE({ ...e, answer_summary: x.target.value })} /></Field></div>
                  <Field label="Related expertise slugs (comma-separated)"><Input data-testid="blog-input-related-expertise" value={(e.related_expertise || []).join(", ")} onChange={(x) => setE({ ...e, related_expertise: x.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} /></Field>
                  <Field label="Related article slugs (comma-separated)"><Input data-testid="blog-input-related-articles" value={(e.related_articles || []).join(", ")} onChange={(x) => setE({ ...e, related_articles: x.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} /></Field>
                  {/* --- Hindi translation (optional) --- */}
                  <div className="col-span-2 border-t border-border pt-4 mt-2">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">हिन्दी Publication</div>
                        <div className="text-[11px] text-muted-foreground/80 mt-1">Toggle to add a Hindi version. Article will be served at <code>?lang=hi</code> with proper hreflang for Google.</div>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <HindiTranslateButton draft={e} setDraft={setE} />
                        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                          <input data-testid="blog-input-has-hindi" type="checkbox" checked={!!e.has_hindi} onChange={(x) => setE({ ...e, has_hindi: x.target.checked })} className="w-4 h-4" />
                          <span>Publish in Hindi</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  {e.has_hindi && (
                    <>
                      <div className="col-span-2"><Field label="Hindi Title (हिन्दी शीर्षक)"><Input data-testid="blog-input-hindi-title" value={e.hindi_title || ""} onChange={(x) => setE({ ...e, hindi_title: x.target.value })} placeholder="मोतियाबिंद में लेंस चयन 2026" /></Field></div>
                      <div className="col-span-2"><Field label="Hindi Excerpt (सारांश)"><Textarea data-testid="blog-input-hindi-excerpt" rows={2} value={e.hindi_excerpt || ""} onChange={(x) => setE({ ...e, hindi_excerpt: x.target.value })} /></Field></div>
                      <div className="col-span-2"><Field label="Hindi Body (हिन्दी लेख)" hint="Rich text — same toolbar as English. Old markdown posts continue to render correctly."><RichTextEditor value={e.hindi_body || ""} onChange={(html) => setE({ ...e, hindi_body: html })} placeholder="हिन्दी लेख यहाँ लिखें…" testId="blog-input-hindi-body" minHeight={320} /></Field></div>
                      <div className="col-span-2"><Field label="Hindi Answer summary (हिन्दी TL;DR)"><Textarea data-testid="blog-input-hindi-answer" rows={3} value={e.hindi_answer_summary || ""} onChange={(x) => setE({ ...e, hindi_answer_summary: x.target.value })} /></Field></div>
                    </>
                  )}
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="hub" className="mt-6">
            <Section
              collection="hub"
              blank={{ slug: "", title: "", category: "Disease Library", excerpt: "", cover_image: "", reading_time: 5, overview: "", symptoms: [], causes: [], risk_factors: [], diagnosis: "", treatment: "", when_to_see: "", prevention: "", faqs: [], references: [], answer_summary: "", medically_reviewed: "", related_expertise: [], related_articles: [] }}
              renderForm={(e, setE) => (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Slug" hint={`URL: /hub/${slugifyClient(e.slug || e.title) || "auto-generated on save"} — Hindi & spaces auto-normalised on save.`}><Input value={e.slug} onChange={(x) => setE({ ...e, slug: x.target.value })} placeholder="Leave blank to auto-generate from title" /></Field>
                  <Field label="Category"><Input value={e.category} onChange={(x) => setE({ ...e, category: x.target.value })} /></Field>
                  <div className="col-span-2"><Field label="Title"><Input value={e.title} onChange={(x) => {
                    const wasAutoSlug = !e.slug || e.slug === slugifyClient(e.title);
                    setE({ ...e, title: x.target.value, ...(wasAutoSlug ? { slug: slugifyClient(x.target.value) } : {}) });
                  }} /></Field></div>
                  <div className="col-span-2"><Field label="Excerpt"><Textarea rows={2} value={e.excerpt} onChange={(x) => setE({ ...e, excerpt: x.target.value })} /></Field></div>
                  <Field label="Cover image URL"><Input value={e.cover_image} onChange={(x) => setE({ ...e, cover_image: x.target.value })} placeholder="Paste URL or upload below" /></Field>
                  <Field label="Reading time"><Input type="number" value={e.reading_time} onChange={(x) => setE({ ...e, reading_time: parseInt(x.target.value || 0, 10) })} /></Field>
                  <div className="col-span-2"><ImageUpload label="Or upload cover image" value={e.cover_image} onChange={(url) => setE({ ...e, cover_image: url })} /></div>
                  <div className="col-span-2"><Field label="Overview"><Textarea rows={4} value={e.overview} onChange={(x) => setE({ ...e, overview: x.target.value })} /></Field></div>
                  <Field label="Symptoms (one per line)"><Textarea rows={4} value={(e.symptoms || []).join("\n")} onChange={(x) => setE({ ...e, symptoms: x.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} /></Field>
                  <Field label="Causes (one per line)"><Textarea rows={4} value={(e.causes || []).join("\n")} onChange={(x) => setE({ ...e, causes: x.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} /></Field>
                  <Field label="Risk factors (one per line)"><Textarea rows={4} value={(e.risk_factors || []).join("\n")} onChange={(x) => setE({ ...e, risk_factors: x.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} /></Field>
                  <Field label="References (one per line)"><Textarea rows={4} value={(e.references || []).join("\n")} onChange={(x) => setE({ ...e, references: x.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} /></Field>
                  <div className="col-span-2"><Field label="Diagnosis"><Textarea rows={3} value={e.diagnosis} onChange={(x) => setE({ ...e, diagnosis: x.target.value })} /></Field></div>
                  <div className="col-span-2"><Field label="Treatment"><Textarea rows={3} value={e.treatment} onChange={(x) => setE({ ...e, treatment: x.target.value })} /></Field></div>
                  <div className="col-span-2"><Field label="When to see a doctor"><Textarea rows={2} value={e.when_to_see} onChange={(x) => setE({ ...e, when_to_see: x.target.value })} /></Field></div>
                  <div className="col-span-2"><Field label="Prevention"><Textarea rows={2} value={e.prevention} onChange={(x) => setE({ ...e, prevention: x.target.value })} /></Field></div>
                  <div className="col-span-2"><Field label="Answer summary (TL;DR for AI search)"><Textarea rows={3} value={e.answer_summary || ""} onChange={(x) => setE({ ...e, answer_summary: x.target.value })} /></Field></div>
                  <Field label="Medically reviewed"><Input type="date" value={e.medically_reviewed || ""} onChange={(x) => setE({ ...e, medically_reviewed: x.target.value })} /></Field>
                  <Field label="Related expertise slugs (comma-separated)"><Input value={(e.related_expertise || []).join(", ")} onChange={(x) => setE({ ...e, related_expertise: x.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} /></Field>
                  <div className="col-span-2"><Field label="Related article slugs (comma-separated)"><Input value={(e.related_articles || []).join(", ")} onChange={(x) => setE({ ...e, related_articles: x.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} /></Field></div>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="faqs" className="mt-6">
            <FaqSection />
          </TabsContent>

          <TabsContent value="testimonials" className="mt-6">
            <Section
              collection="testimonials"
              blank={BLANK.testimonials}
              renderForm={(e, setE) => (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Name"><Input value={e.name} onChange={(x) => setE({ ...e, name: x.target.value })} /></Field>
                  <Field label="Location"><Input value={e.location} onChange={(x) => setE({ ...e, location: x.target.value })} /></Field>
                  <Field label="Procedure"><Input value={e.procedure} onChange={(x) => setE({ ...e, procedure: x.target.value })} /></Field>
                  <Field label="Rating (1-5)"><Input type="number" min={1} max={5} value={e.rating} onChange={(x) => setE({ ...e, rating: parseInt(x.target.value || 5, 10) })} /></Field>
                  <div className="col-span-2"><Field label="Quote"><Textarea rows={4} value={e.quote} onChange={(x) => setE({ ...e, quote: x.target.value })} /></Field></div>
                  <Field label="YouTube video URL or ID (optional)" hint="Paste a full YouTube URL or an 11-character video ID. Leave blank for text-only testimonial."><Input value={e.video_id || ""} onChange={(x) => setE({ ...e, video_id: x.target.value })} placeholder="https://youtu.be/…" /></Field>
                  <Field label="Video title (optional)"><Input value={e.video_title || ""} onChange={(x) => setE({ ...e, video_title: x.target.value })} /></Field>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="media" className="mt-6">
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap p-3 rounded-lg border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5">
              <div className="text-xs">
                <div className="font-medium text-[hsl(var(--foreground))]">Publications enrichment</div>
                <div className="text-muted-foreground mt-0.5">Auto-fetches PMID · DOI · citation counts for rows with <code>type=publication</code> from Semantic Scholar → PubMed E-Utilities (year-guarded ±2y).</div>
              </div>
              <RefreshPublicationsBtn />
            </div>
            <Section
              collection="media"
              blank={BLANK.media}
              renderForm={(e, setE) => (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Type"><select className="w-full border border-input rounded-md h-10 px-3 bg-background" value={e.type} onChange={(x) => setE({ ...e, type: x.target.value })}><option value="article">Article</option><option value="interview">Interview</option><option value="talk">Talk</option><option value="video">Video</option><option value="publication">Publication</option></select></Field>
                  <Field label="Outlet"><Input value={e.outlet} onChange={(x) => setE({ ...e, outlet: x.target.value })} /></Field>
                  <div className="col-span-2"><Field label="Title"><Input value={e.title} onChange={(x) => setE({ ...e, title: x.target.value })} /></Field></div>
                  <Field label="Date"><Input type="date" value={e.date} onChange={(x) => setE({ ...e, date: x.target.value })} /></Field>
                  <Field label="Link"><Input value={e.link} onChange={(x) => setE({ ...e, link: x.target.value })} /></Field>
                  <div className="col-span-2"><Field label="Summary"><Textarea rows={3} value={e.summary} onChange={(x) => setE({ ...e, summary: x.target.value })} /></Field></div>
                  {e.type === "publication" && (
                    <div className="col-span-2 text-[11px] p-3 rounded-lg bg-muted/40 border border-border/60 flex items-center gap-4 flex-wrap">
                      <span className="uppercase tracking-[0.16em] text-muted-foreground">Auto-enriched</span>
                      <span>PMID: <code>{e.pmid || "—"}</code></span>
                      <span>DOI: <code>{e.doi || "—"}</code></span>
                      <span>Cited by: <code>{e.citations ?? "—"}</code></span>
                      {e.citations_updated_at && <span className="text-muted-foreground opacity-70">Updated {e.citations_updated_at.slice(0, 10)}</span>}
                    </div>
                  )}
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="shorts" className="mt-6">
            <Section
              collection="shorts"
              blank={BLANK.shorts}
              renderForm={(e, setE) => {
                // Live preview of the detected platform / embed URL so the admin
                // knows the link was parsed correctly before hitting Save.
                const preview = detectShortClient(e.url || "");
                return (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Field
                        label="Video URL"
                        hint="Paste a YouTube Short (youtube.com/shorts/…) or an Instagram Reel (instagram.com/reel/…). Everything else is auto-detected."
                      >
                        <Input
                          data-testid="shorts-input-url"
                          value={e.url}
                          onChange={(x) => setE({ ...e, url: x.target.value })}
                          placeholder="https://www.youtube.com/shorts/…  or  https://www.instagram.com/reel/…"
                        />
                      </Field>
                    </div>
                    {/* Detection badge */}
                    <div className="col-span-2">
                      {e.url ? (
                        preview.platform ? (
                          <div className="flex items-center gap-3 flex-wrap p-3 rounded-lg border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5">
                            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--primary))]">
                              {preview.platform === "youtube_short" ? "YouTube Short" : "Instagram Reel"}
                            </span>
                            <span className="text-xs text-muted-foreground">id: <code>{preview.video_id}</code></span>
                            {preview.thumbnail && (
                              <img src={preview.thumbnail} alt="thumb" className="h-14 rounded-md border border-border" loading="lazy" />
                            )}
                          </div>
                        ) : (
                          <div className="text-[11px] text-destructive">URL not recognised — please paste a full YouTube Short or Instagram Reel URL.</div>
                        )
                      ) : null}
                    </div>
                    <div className="col-span-2"><Field label="Caption (optional, shown under the video)"><Textarea data-testid="shorts-input-caption" rows={2} value={e.caption || ""} onChange={(x) => setE({ ...e, caption: x.target.value })} placeholder="Cataract myth #1 — you don't have to wait for the cataract to 'mature'." /></Field></div>
                    <Field label="Title (optional)"><Input value={e.title || ""} onChange={(x) => setE({ ...e, title: x.target.value })} /></Field>
                    <Field label="Date (optional)"><Input type="date" value={e.date || ""} onChange={(x) => setE({ ...e, date: x.target.value })} /></Field>
                    <Field label="Featured"><select data-testid="shorts-input-featured" className="w-full border border-input rounded-md h-10 px-3 bg-background" value={e.featured ? "1" : "0"} onChange={(x) => setE({ ...e, featured: x.target.value === "1" })}><option value="0">No</option><option value="1">Yes — pin to top</option></select></Field>
                    <Field label="Order (lower = earlier)"><Input type="number" value={e.order || 0} onChange={(x) => setE({ ...e, order: parseInt(x.target.value || 0, 10) })} /></Field>
                  </div>
                );
              }}
            />
          </TabsContent>

          <TabsContent value="locations" className="mt-6">
            <Section
              collection="locations"
              blank={BLANK.locations}
              renderForm={(e, setE) => (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Slug (city name in lowercase)"><Input value={e.slug} onChange={(x) => setE({ ...e, slug: x.target.value })} /></Field>
                  <Field label="City"><Input value={e.city} onChange={(x) => setE({ ...e, city: x.target.value })} /></Field>
                  <Field label="State"><Input value={e.state || ""} onChange={(x) => setE({ ...e, state: x.target.value })} /></Field>
                  <Field label="Distance (e.g. ~60 km from Varanasi)"><Input value={e.distance_km || ""} onChange={(x) => setE({ ...e, distance_km: x.target.value })} /></Field>
                  <div className="col-span-2"><Field label="Tagline"><Input value={e.tagline || ""} onChange={(x) => setE({ ...e, tagline: x.target.value })} /></Field></div>
                  <div className="col-span-2"><Field label="Intro paragraph"><Textarea rows={4} value={e.intro || ""} onChange={(x) => setE({ ...e, intro: x.target.value })} /></Field></div>
                  <div className="col-span-2"><Field label="Travel note (one line)"><Input value={e.travel_note || ""} onChange={(x) => setE({ ...e, travel_note: x.target.value })} /></Field></div>
                  <Field label="Latitude"><Input type="number" step="0.0001" value={e.lat ?? ""} onChange={(x) => setE({ ...e, lat: x.target.value ? parseFloat(x.target.value) : null })} /></Field>
                  <Field label="Longitude"><Input type="number" step="0.0001" value={e.lng ?? ""} onChange={(x) => setE({ ...e, lng: x.target.value ? parseFloat(x.target.value) : null })} /></Field>
                  <div className="col-span-2"><Field label="Landmarks / areas served (one per line)"><Textarea rows={3} value={(e.landmarks || []).join("\n")} onChange={(x) => setE({ ...e, landmarks: x.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} /></Field></div>
                  <div className="col-span-2"><Field label='FAQs (JSON — array of {"q":"...","a":"..."})'><Textarea rows={6} value={JSON.stringify(e.faqs || [], null, 2)} onChange={(x) => { try { setE({ ...e, faqs: JSON.parse(x.target.value) }); } catch { /* ignore */ } }} /></Field></div>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <Section
              collection="reviews"
              blank={BLANK.reviews}
              renderForm={(e, setE) => (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Reviewer name"><Input value={e.author_name} onChange={(x) => setE({ ...e, author_name: x.target.value })} /></Field>
                  <Field label="Rating (1-5)"><Input type="number" min={1} max={5} value={e.rating} onChange={(x) => setE({ ...e, rating: parseInt(x.target.value || 5, 10) })} /></Field>
                  <Field label="Relative time (e.g. '2 weeks ago')"><Input value={e.relative_time || ""} onChange={(x) => setE({ ...e, relative_time: x.target.value })} /></Field>
                  <Field label="Source"><select className="w-full border border-input rounded-md h-10 px-3 bg-background text-sm" value={e.source || "manual"} onChange={(x) => setE({ ...e, source: x.target.value })}><option value="manual">Manual (typed by admin)</option><option value="google">Google (imported)</option></select></Field>
                  <Field label="Profile photo URL (optional)"><Input value={e.profile_photo_url || ""} onChange={(x) => setE({ ...e, profile_photo_url: x.target.value })} /></Field>
                  <Field label="Display order (lower first)"><Input type="number" value={e.order || 0} onChange={(x) => setE({ ...e, order: parseInt(x.target.value || 0, 10) })} /></Field>
                  <div className="col-span-2"><Field label="Review text" hint="Paste the customer's Google review verbatim. Emojis allowed."><Textarea rows={5} value={e.text} onChange={(x) => setE({ ...e, text: x.target.value })} /></Field></div>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="guides" className="mt-6">
            <Section
              collection="guides"
              blank={BLANK.guides}
              renderForm={(e, setE) => (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Slug (url-safe, e.g. cataract-guide)"><Input value={e.slug} onChange={(x) => setE({ ...e, slug: x.target.value })} /></Field>
                  <Field label="Category"><Input value={e.category || "General"} onChange={(x) => setE({ ...e, category: x.target.value })} /></Field>
                  <div className="col-span-2"><Field label="Title"><Input value={e.title} onChange={(x) => setE({ ...e, title: x.target.value })} /></Field></div>
                  <div className="col-span-2"><Field label="Description"><Textarea rows={3} value={e.description} onChange={(x) => setE({ ...e, description: x.target.value })} /></Field></div>
                  <div className="col-span-2"><Field label="Guide PDF"><ImageUpload value={e.file_url} onChange={(url) => setE({ ...e, file_url: url })} accept=".pdf,application/pdf" /></Field></div>
                  <div className="col-span-2"><Field label="Cover image"><ImageUpload value={e.cover_image} onChange={(url) => setE({ ...e, cover_image: url })} /></Field></div>
                  <Field label="Pages"><Input type="number" value={e.pages || 0} onChange={(x) => setE({ ...e, pages: parseInt(x.target.value || 0, 10) })} /></Field>
                  <Field label="Language"><select className="w-full border border-input rounded-md h-10 px-3 bg-background text-sm" value={e.language || "en"} onChange={(x) => setE({ ...e, language: x.target.value })}><option value="en">English</option><option value="hi">Hindi</option></select></Field>
                  <Field label="Display order"><Input type="number" value={e.order || 0} onChange={(x) => setE({ ...e, order: parseInt(x.target.value || 0, 10) })} /></Field>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="cases" className="mt-6">
            <Section
              collection="cases"
              blank={BLANK.cases}
              renderForm={(e, setE) => {
                // Single source of truth — CASE_CATEGORIES is exported from
                // /pages/EyeCases.jsx so the admin dropdown and the public
                // filter chips can never drift apart.
                const catOptions = CASE_CATEGORIES.filter((c) => c !== "All");
                const setLink = (idx, patch) => {
                  const links = [...(e.external_links || [])];
                  links[idx] = { ...links[idx], ...patch };
                  setE({ ...e, external_links: links });
                };
                const removeLink = (idx) => {
                  const links = [...(e.external_links || [])];
                  links.splice(idx, 1);
                  setE({ ...e, external_links: links });
                };
                const addLink = () =>
                  setE({ ...e, external_links: [...(e.external_links || []), { label: "", url: "" }] });
                const setExtra = (idx, patch) => {
                  const imgs = [...(e.extra_images || [])];
                  imgs[idx] = { ...imgs[idx], ...patch };
                  setE({ ...e, extra_images: imgs });
                };
                const removeExtra = (idx) => {
                  const imgs = [...(e.extra_images || [])];
                  imgs.splice(idx, 1);
                  setE({ ...e, extra_images: imgs });
                };
                const addExtra = () =>
                  setE({ ...e, extra_images: [...(e.extra_images || []), { url: "", caption: "" }] });
                return (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Slug" hint={`URL: /eye-cases/${slugifyClient(e.slug || e.title) || "auto-generated on save"}`}>
                      <Input
                        data-testid="cases-input-slug"
                        value={e.slug}
                        onChange={(x) => setE({ ...e, slug: x.target.value })}
                        placeholder="Leave blank to auto-generate from title"
                      />
                    </Field>
                    <Field label="Category">
                      <select
                        data-testid="cases-input-category"
                        className="w-full border border-input rounded-md h-10 px-3 bg-background text-sm"
                        value={e.category}
                        onChange={(x) => setE({ ...e, category: x.target.value })}
                      >
                        {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Field>
                    <div className="col-span-2">
                      <Field label="Title" hint="Use the anatomy + finding + modality — e.g. 'Wet AMD on OCT — subretinal fluid'.">
                        <Input
                          data-testid="cases-input-title"
                          value={e.title}
                          onChange={(x) => {
                            const wasAutoSlug = !e.slug || e.slug === slugifyClient(e.title);
                            setE({ ...e, title: x.target.value, ...(wasAutoSlug ? { slug: slugifyClient(x.target.value) } : {}) });
                          }}
                        />
                      </Field>
                    </div>
                    <Field label="Submitted by" hint="Shown as the byline and used for author schema (E-E-A-T signal).">
                      <select
                        data-testid="cases-input-submitted-by"
                        className="w-full border border-input rounded-md h-10 px-3 bg-background text-sm"
                        value={e.submitted_by || "shantanu"}
                        onChange={(x) => setE({ ...e, submitted_by: x.target.value })}
                      >
                        <option value="shantanu">Dr. Shantanu Kumar Gupta</option>
                        <option value="manisha">Dr. Manisha Mishra</option>
                        <option value="team">Clinical team (joint / anonymous)</option>
                      </select>
                    </Field>
                    <Field label="Diagnosis (short, optional)" hint="Rendered as a chip and used for MedicalCondition schema.">
                      <Input
                        data-testid="cases-input-diagnosis"
                        value={e.diagnosis || ""}
                        onChange={(x) => setE({ ...e, diagnosis: x.target.value })}
                        placeholder="e.g. Neovascular (wet) AMD"
                      />
                    </Field>
                    <div className="col-span-2">
                      <Field label="Summary (1-2 lines, shown on card + meta description)">
                        <Textarea
                          data-testid="cases-input-summary"
                          rows={2}
                          value={e.summary || ""}
                          onChange={(x) => setE({ ...e, summary: x.target.value })}
                        />
                      </Field>
                    </div>
                    <div className="col-span-2">
                      <Field label="Patient context (anonymised, optional)" hint="e.g. '62-year-old diabetic, HbA1c 9.4%'. Never include identifiable details.">
                        <Input
                          data-testid="cases-input-patient-context"
                          value={e.patient_context || ""}
                          onChange={(x) => setE({ ...e, patient_context: x.target.value })}
                        />
                      </Field>
                    </div>
                    <div className="col-span-2">
                      <Field label="Primary image (hero)" hint="Drop the clinical picture here — slit-lamp, fundus, OCT, etc.">
                        <ImageUpload value={e.image_url} onChange={(url) => setE({ ...e, image_url: url })} label="" />
                      </Field>
                    </div>
                    <div className="col-span-2">
                      <Field label="Image alt text (mandatory for SEO + accessibility)" hint="Describe what the image actually shows — 'OCT of macula showing sub-retinal fluid and PED'.">
                        <Input
                          data-testid="cases-input-image-alt"
                          value={e.image_alt || ""}
                          onChange={(x) => setE({ ...e, image_alt: x.target.value })}
                        />
                      </Field>
                    </div>
                    <div className="col-span-2">
                      <Field label="Case description" hint="Rich text — findings, interpretation, differentials, teaching pearls. Embed additional images inline if you like.">
                        <RichTextEditor
                          value={e.description || ""}
                          onChange={(html) => setE({ ...e, description: html })}
                          placeholder="Write the case description, interpretation and teaching notes…"
                          testId="cases-input-description"
                          minHeight={340}
                        />
                      </Field>
                    </div>

                    {/* Extra images repeater */}
                    <div className="col-span-2 border-t border-border pt-4 mt-2">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-muted-foreground">Additional images (optional)</div>
                          <div className="text-[11px] text-muted-foreground/80 mt-0.5">Follow-up scans, comparison images, etc. Each rendered with its caption.</div>
                        </div>
                        <button
                          type="button"
                          onClick={addExtra}
                          className="text-xs px-3 py-1.5 rounded-md border border-border hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
                          data-testid="cases-add-extra-image"
                        >
                          + Add image
                        </button>
                      </div>
                      <div className="space-y-4">
                        {(e.extra_images || []).map((im, idx) => (
                          <div key={idx} className="grid grid-cols-[1fr_auto] gap-3 items-start p-3 rounded-lg border border-border">
                            <div className="space-y-2">
                              <ImageUpload value={im.url} onChange={(url) => setExtra(idx, { url })} label={`Image #${idx + 1}`} />
                              <Input
                                value={im.caption || ""}
                                onChange={(x) => setExtra(idx, { caption: x.target.value })}
                                placeholder="Caption (also serves as alt text)"
                                data-testid={`cases-extra-caption-${idx}`}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeExtra(idx)}
                              className="text-xs text-destructive p-2"
                              data-testid={`cases-extra-remove-${idx}`}
                              title="Remove this image"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                        {(e.extra_images || []).length === 0 && (
                          <div className="text-[11px] text-muted-foreground italic">No additional images yet.</div>
                        )}
                      </div>
                    </div>

                    {/* External links repeater */}
                    <div className="col-span-2 border-t border-border pt-4 mt-2">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-muted-foreground">Further reading / references (optional)</div>
                          <div className="text-[11px] text-muted-foreground/80 mt-0.5">Journal articles, EyeWiki entries, atlas figures. Rendered as a link list on the case page.</div>
                        </div>
                        <button
                          type="button"
                          onClick={addLink}
                          className="text-xs px-3 py-1.5 rounded-md border border-border hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
                          data-testid="cases-add-external-link"
                        >
                          + Add link
                        </button>
                      </div>
                      <div className="space-y-2">
                        {(e.external_links || []).map((l, idx) => (
                          <div key={idx} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
                            <Input
                              value={l.label || ""}
                              onChange={(x) => setLink(idx, { label: x.target.value })}
                              placeholder="Label — e.g. AAO EyeWiki"
                              data-testid={`cases-external-label-${idx}`}
                            />
                            <Input
                              value={l.url || ""}
                              onChange={(x) => setLink(idx, { url: x.target.value })}
                              placeholder="https://…"
                              data-testid={`cases-external-url-${idx}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeLink(idx)}
                              className="text-xs text-destructive p-2"
                              data-testid={`cases-external-remove-${idx}`}
                              title="Remove"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                        {(e.external_links || []).length === 0 && (
                          <div className="text-[11px] text-muted-foreground italic">No external links yet.</div>
                        )}
                      </div>
                    </div>

                    <Field label="Related expertise slugs (comma-separated)" hint="e.g. medical-retina, retina-injections">
                      <Input
                        data-testid="cases-input-related-expertise"
                        value={(e.related_expertise || []).join(", ")}
                        onChange={(x) => setE({ ...e, related_expertise: x.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                      />
                    </Field>
                    <Field label="Order (lower first)">
                      <Input type="number" value={e.order || 0} onChange={(x) => setE({ ...e, order: parseInt(x.target.value || 0, 10) })} />
                    </Field>
                    <Field label="Published">
                      <select
                        className="w-full border border-input rounded-md h-10 px-3 bg-background text-sm"
                        value={e.published ? "1" : "0"}
                        onChange={(x) => setE({ ...e, published: x.target.value === "1" })}
                        data-testid="cases-input-published"
                      >
                        <option value="1">Yes — visible on /eye-cases</option>
                        <option value="0">No — save as draft</option>
                      </select>
                    </Field>
                    <Field label="Featured">
                      <select
                        className="w-full border border-input rounded-md h-10 px-3 bg-background text-sm"
                        value={e.featured ? "1" : "0"}
                        onChange={(x) => setE({ ...e, featured: x.target.value === "1" })}
                        data-testid="cases-input-featured"
                      >
                        <option value="0">No</option>
                        <option value="1">Yes — pin to top</option>
                      </select>
                    </Field>
                  </div>
                );
              }}
            />
          </TabsContent>

          <TabsContent value="appointments" className="mt-6">
            <div className="border border-border rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Concern</TableHead>
                    <TableHead>Preferred</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appointments.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.name}</TableCell>
                      <TableCell>{a.phone}</TableCell>
                      <TableCell className="max-w-xs truncate">{a.concern}</TableCell>
                      <TableCell>{a.preferred_date}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{a.created_at?.slice(0, 16)}</TableCell>
                    </TableRow>
                  ))}
                  {appointments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No appointments yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="symptom-analytics" className="mt-6">
            <SymptomAnalytics />
          </TabsContent>

          <TabsContent value="gmb-studio" className="mt-6">
            <GMBPostStudio />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
